/**
 * GCS direct-upload service for customer slip uploads.
 *
 * Flow:
 *   1. POST /api/web/upload-slip-url  → receive { uploadUrl, objectPath }
 *   2. PUT slip image directly to GCS using the signed URL
 *   3. POST /api/web/upload-slip-complete → receive { url, id }
 *
 * The backend never touches the slip bytes; it only authenticates the user,
 * signs the URL with the userId embedded in the object path, validates the
 * uploaded object, and creates/updates the files-merge Firestore document.
 */

const API_BASE = `${process.env.REACT_APP_API_URL}/api/web`

export interface SlipUploadURLResponse {
    uploadUrl: string
    objectPath: string
    expiresIn: number
}

export interface SlipUploadResult {
    url: string
    id: string
}

// ─── Step 1: request a signed URL ────────────────────────────────────────────

async function requestSlipSignedUrl(
    file: File,
    productId: string,
    refId: string,
    token: string
): Promise<SlipUploadURLResponse> {
    const res = await fetch(`${API_BASE}/upload-slip-url`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
            contentType: file.type,
            productId,
            refId,
        }),
    })

    if (!res.ok) {
        const body = await res.json().catch(() => ({ error: res.statusText }))
        throw new Error(body.error || `Failed to get slip upload URL (${res.status})`)
    }

    return res.json()
}

// ─── Step 2: PUT directly to GCS ─────────────────────────────────────────────

function uploadSlipToGCS(
    file: File,
    uploadUrl: string,
    onProgress?: (percent: number) => void
): Promise<void> {
    return new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        xhr.open('PUT', uploadUrl, true)
        xhr.setRequestHeader('Content-Type', file.type)
        xhr.timeout = 5 * 60 * 1000 // 5-minute hard limit for slip images

        if (onProgress) {
            xhr.upload.onprogress = (e) => {
                if (e.lengthComputable) {
                    onProgress(Math.round((e.loaded / e.total) * 100))
                }
            }
        }

        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve()
            } else {
                reject(
                    new Error(
                        `GCS upload failed: ${xhr.status} ${xhr.statusText}`
                    )
                )
            }
        }

        xhr.onerror = () => reject(new Error('Network error during upload'))
        xhr.ontimeout = () => reject(new Error('Upload timed out'))

        xhr.send(file)
    })
}

// ─── Step 3: notify backend ───────────────────────────────────────────────────

async function completeSlipUpload(
    objectPath: string,
    contentType: string,
    size: number,
    productId: string,
    refId: string,
    token: string
): Promise<SlipUploadResult> {
    const res = await fetch(`${API_BASE}/upload-slip-complete`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
            objectPath,
            contentType,
            size,
            productId,
            refId,
        }),
    })

    if (!res.ok) {
        const body = await res.json().catch(() => ({ error: res.statusText }))
        throw new Error(body.error || `Upload completion failed (${res.status})`)
    }

    return res.json()
}

// ─── Convenience function used by the slip upload component ──────────────────

/**
 * Executes the full 3-step signed-URL slip upload flow.
 *
 * @param file       The slip image File.
 * @param productId  The product being purchased.
 * @param refId      Existing files-merge doc ID, or '' to create a new one.
 * @param onProgress Optional progress callback (0–100).
 * @returns          { url: Firebase Storage URL, id: files-merge doc ID }
 */
export async function uploadSlipToBackend(
    file: File,
    productId: string,
    refId: string,
    onProgress?: (percent: number) => void
): Promise<SlipUploadResult> {
    const token = window.localStorage.getItem('access_token') ?? ''

    // Step 1
    const { uploadUrl, objectPath } = await requestSlipSignedUrl(
        file,
        productId,
        refId,
        token
    )

    // Step 2
    await uploadSlipToGCS(file, uploadUrl, onProgress)

    // Step 3
    return completeSlipUpload(
        objectPath,
        file.type,
        file.size,
        productId,
        refId,
        token
    )
}
